import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RequestState } from 'src/domain/request'
import { Authorization, ForbiddenErrorCodeEnum } from 'src/slices/services/api'
import AuthenticatedApi from 'src/slices/services/authenticatedApi'
import serializeError from 'src/slices/services/error'
// eslint-disable-next-line import/no-cycle
import { AppThunkConfig, RejectedAction } from 'src/store'
import { purge } from 'src/store/action'

// FIXME: token(firebase auth)とauthorization(データベースの値)は
// 関連性の全くないデータなので、同一のsliceの中で管理する必要が無いので分離する
export interface AuthState extends RequestState {
  token: string
  authorizations: Authorization[]
  currentAuthorization: Authorization | null
}

export const initialState: AuthState = {
  status: 'idle',
  token: '',
  authorizations: [],
  currentAuthorization: null,
}

export const postAuth = createAsyncThunk<Authorization[], void, AppThunkConfig>(
  'auth/postAuth',
  async (_, { getState }) => {
    const { auth } = getState()
    const response = await new AuthenticatedApi(auth.token).postAuth()
    return response.data
  },
  { serializeError }
)

type Token = string
type OrganizationId = string

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateToken: (state, { payload }: PayloadAction<Token>): AuthState => {
      if (state.token === payload) {
        return state
      }
      // リロード直後の postAuth は localStorage から復元した古いトークンで走るため、
      // 期限切れなどで失敗すると status が failed のまま再取得されず、
      // 認可の結果（ネットワーク制限を含む）が画面に反映されないまま古い表示が残る。
      // Firebase から新しいトークンを受け取った時点で idle に戻し、取り直させる。
      return {
        ...state,
        token: payload,
        status: 'idle',
        error: undefined,
      }
    },
    removeToken: (state): AuthState => {
      return {
        ...state,
        token: '',
      }
    },
    setCurrentAuthorization: (
      state,
      { payload }: PayloadAction<OrganizationId>
    ): AuthState => {
      const auth = state.authorizations.find(
        (item) => item.organization.id === payload
      )
      return {
        ...state,
        currentAuthorization: auth ?? null,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(purge, () => {
      return initialState
    })
    builder.addCase(postAuth.pending, (state) => {
      return {
        ...state,
        status: 'loading',
        // 再取得のたびに前回の失敗理由を捨てる（許可ネットワークに戻したあとも
        // 案内画面に貼り付いたままになるのを防ぐ）
        error: undefined,
      }
    })
    builder.addCase(
      postAuth.fulfilled,
      (state, { payload }: PayloadAction<Authorization[]>) => {
        // postAuth はトークン更新のたびに再実行されるため、選択中の org が一覧に残って
        // いる限りは利用停止中でも維持する（勝手に別 org へ切り替わると、URL の org と
        // 画面の権限がずれたまま操作できてしまう）。
        // 選択中の org が無い（初回ログイン・IP 制限で除外された等）ときだけ、利用停止中
        // の org を繰り上げないよう利用中の org から選ぶ。利用中が1件も無いときは従来
        // どおり先頭を選び、利用停止中であることの案内を出す。
        let authorizationForOrg: Authorization | undefined
        const currentOrganizationId =
          state.currentAuthorization?.organization?.id
        if (currentOrganizationId) {
          authorizationForOrg = payload.find(
            (item) => item.organization.id === currentOrganizationId
          )
        }
        const activeAuthorizations = payload.filter((item) => item.active)
        return {
          ...state,
          status: 'succeeded',
          error: undefined,
          authorizations: payload,
          currentAuthorization:
            authorizationForOrg || activeAuthorizations[0] || payload[0],
        }
      }
    )
    // 失敗の理由（ネットワーク制限かどうか）を画面側で出し分けるため error を保持する
    builder.addCase(postAuth.rejected, (state, { error }) => {
      return {
        ...state,
        status: 'failed',
        error,
      }
    })
    // ログイン後にネットワークが切り替わった場合、postAuth は再実行されないため
    // 認証時のエラーだけでは検知できない。制限は全データ API に効いており
    // network_restricted が返るので、どの thunk の失敗でも拾って案内に切り替える。
    builder.addMatcher<RejectedAction>(
      (action) => action.type.endsWith('/rejected'),
      (state, { error }) => {
        if (error?.code !== ForbiddenErrorCodeEnum.NetworkRestricted) {
          return state
        }
        return {
          ...state,
          error,
        }
      }
    )
  },
})

export const { updateToken, removeToken, setCurrentAuthorization } =
  authSlice.actions

export default authSlice.reducer
